import { useContext, useEffect, useState } from "react";
import { ethers } from "ethers";
import {
  Alert,
  Box,
  Button,
  Fade,
  IconButton,
  MenuItem,
  Paper,
  Select,
  Stack,
  Typography,
} from "@mui/material";
import { useSigner, useContract } from "wagmi";
import CurrencyExchangeIcon from "@mui/icons-material/CurrencyExchange";
import LocalAtmIcon from "@mui/icons-material/LocalAtm";

import {
  getDefaultInputs,
  MODULO_COINFLIP,
  MODULO_ETHEROLL,
  MODULO_ROLLADICE,
  MODULO_ROULETTE,
  MODULO_TWODICE,
} from "../../games";
import { getDiceWinAmount, getWinningChance, parseAmount } from "../../utils";
import History from "../History";
import GameAnimation from "./Game/GameAnimation";
import GameInputs from "./Game/GameInputs";
import SessionContext from "../../contexts/session";
import ButtonBet from "./Game/ButtonBet";
import GameSettings from "./Game/GameSettings";
import { useTranslation, Trans } from "react-i18next";
import AnimatedNumbers from "react-animated-numbers";

import AddRoundedIcon from "@mui/icons-material/AddRounded";
import RemoveRoundedIcon from "@mui/icons-material/RemoveRounded";
import ButtonConnect from "../ButtonConnect";
import { getPossibleRewards } from "../../models/n2wcalls";

export default function Game({ game }) {
  const { t } = useTranslation();
  return (
    <>
      <Stack spacing={1}>
        <Stack direction="row" sx={{ alignItems: "baseline" }}>
          <Typography variant="h6">
            <Box
              component={"img"}
              src={game.iconUrl}
              height={24}
              sx={{ verticalAlign: "middle", marginRight: 1 }}
            />
            {t(game.name)}
          </Typography>
          <GameSettings />
        </Stack>
        <GameArea game={game} />
      </Stack>
      <History game={game} />
    </>
  );
}

function GameArea({ game }) {
  const {
    account,
    selectedNetwork,
    settings,
    isAwaitingWallet,
    hasInsufficientFunds,
    isBelowMinimumBet,
    setHasInsufficientFunds,
    setIsBelowMinimumBet,
  } = useContext(SessionContext);
  const { data: signer } = useSigner({ chainId: selectedNetwork.chainIdDec });
  const n2wContract = useContract({
    address: selectedNetwork.contract.address,
    abi: selectedNetwork.contract.abi,
    signerOrProvider: signer,
  });
  const [inputs, setInputs] = useState(() => getDefaultInputs(game.modulo));

  const [betAmount, setBetAmount] = useState(0);
  const [winAmount, setWinAmount] = useState(ethers.BigNumber.from(0));
  const [winningChance, setWinningChance] = useState(0);
  const [multiplier, setMultiplier] = useState(0);
  const [reward, setReward] = useState(0);

  const [isProfitTooSmall, setIsProfitTooSmall] = useState(false);
  const [isWinAmountTooBig, setIsWinAmountTooBig] = useState(false);
  const [hasNoInput, setHasNoInput] = useState(false);
  const [hasTooManyInputs, setHasTooManyInputs] = useState(false);

  const { t } = useTranslation();

  let gameError = "";
  let walletError;
  if (hasNoInput) {
    gameError = t("No input selected");
  } else if (hasTooManyInputs) {
    gameError = t("Too many inputs selected");
  } else if (isProfitTooSmall) {
    gameError = t("Profit is too small");
  } else if (isWinAmountTooBig) {
    gameError = t("Win amount is too big");
  }

  if (hasInsufficientFunds) {
    walletError = t("Insufficient funds in wallet");
  }
  if (isBelowMinimumBet) {
    walletError = t("Amount is lower than the minimum bet accepted");
  }

  function handleBetAmountChange(event) {
    const value = event.target.value;
    if (value === "") {
      setBetAmount("");
      return;
    }
    setBetAmount(Number(event.target.value));
  }

  function handleMultiply(multiplier) {
    setBetAmount((betAmount) => {
      const newAmount = (betAmount * multiplier).toFixed(
        selectedNetwork.ui.decimals
      );
      return Number(newAmount);
    });
  }

  function handlePlus() {
    setBetAmount((betAmount) => {
      const newAmount = (betAmount + selectedNetwork.ui.steps).toFixed(
        selectedNetwork.ui.decimals
      );

      return Number(newAmount);
    });
  }

  function handleMinus() {
    setBetAmount((betAmount) => {
      const newAmount = (betAmount - selectedNetwork.ui.steps).toFixed(
        selectedNetwork.ui.decimals
      );
      if (newAmount <= 0) {
        return betAmount;
      }
      return Number(newAmount);
    });
  }

  useEffect(() => {
    let betAmount_ = isNaN(betAmount) ? 0 : Number(betAmount);
    if (betAmount_ === 0) {
      betAmount_ = selectedNetwork.ui.betInput;
    }
    const winAmount = getDiceWinAmount(
      selectedNetwork,
      inputs,
      ethers.utils.parseUnits(String(betAmount_)),
      game.modulo
    );

    const fetchReward = async () => {
      const reward = await getPossibleRewards(betAmount);
      setReward(parseFloat(reward));
    };

    fetchReward();

    setWinAmount(winAmount);
    setMultiplier(
      (ethers.utils.formatEther(winAmount) / betAmount_).toFixed(2)
    );
    setWinningChance((getWinningChance(game, inputs) * 100).toFixed(2));

    const profit = winAmount.sub(ethers.utils.parseUnits(String(betAmount_)));
    setHasNoInput(inputs.length === 0);
    setHasTooManyInputs(game.maxSelected && inputs.length > game.maxSelected);
    setIsProfitTooSmall(profit.lte(ethers.BigNumber.from(0)));
    if (settings) {
      setIsWinAmountTooBig(
        winAmount.gt(ethers.BigNumber.from(settings.maxProfit))
      );
    }
  }, [
    betAmount,
    game,
    inputs,
    selectedNetwork,
    setHasInsufficientFunds,
    setIsBelowMinimumBet,
    settings,
    n2wContract,
  ]);

  useEffect(() => {
    setHasInsufficientFunds(false);
  }, [
    betAmount,
    inputs,
    selectedNetwork,
    setHasInsufficientFunds,
    setIsBelowMinimumBet,
    account,
  ]);

  useEffect(() => {
    setInputs(getDefaultInputs(game.modulo));
  }, [game]);

  useEffect(() => {
    setBetAmount(selectedNetwork.ui.betInput);
  }, [selectedNetwork]);

  return (
    <Stack
      component={Paper}
      variant="outlined"
      direction={{ xs: "column", md: "row" }}
      sx={{ borderRadius: 2 }}
    >
      <Stack
        elevation={12}
        sx={{
          background: "#4c4c4c",
          p: 4,
          order: { xs: 1, md: 0 },
          width: { xs: "auto", md: "260px" },
          position: "relative",
          borderRadius: 2,
          justifyContent: "center",
        }}
      >
        <Fade
          in={isAwaitingWallet || Boolean(gameError) || Boolean(walletError)}
        >
          <Alert
            severity={isAwaitingWallet ? "warning" : "error"}
            variant="filled"
            sx={{
              position: "absolute",
              left: { xs: 0, md: "260px" },
              whiteSpace: { xs: "normal", md: "nowrap" },
              width: { xs: "100%", md: "auto" },
              top: 0,
            }}
          >
            {isAwaitingWallet
              ? t("Please confirm bet in wallet...")
              : gameError || walletError}
          </Alert>
        </Fade>
        <Stack spacing={4}>
          <Stack spacing={1}>
            <Typography color="secondary">
              <CurrencyExchangeIcon
                fontSize="small"
                sx={{ verticalAlign: "middle", mr: 0.5 }}
              />
              <Trans t={t}>Currency</Trans>
            </Typography>
            <Select value={selectedNetwork.ui.symbol}>
              <MenuItem value={selectedNetwork.ui.symbol} selected>
                <Box
                  component="img"
                  src={selectedNetwork.ui.curIconUrl}
                  height={24}
                  sx={{ verticalAlign: "middle", mr: 0.5 }}
                />
                {selectedNetwork.ui.symbol}
              </MenuItem>
            </Select>
          </Stack>
          <Stack spacing={1}>
            <Typography color="secondary">
              <LocalAtmIcon
                fontSize="small"
                sx={{ verticalAlign: "middle", mr: 0.5 }}
              />
              <Trans t={t}>Amount</Trans>
            </Typography>
            <Stack
              direction="row"
              spacing={1}
              sx={{
                borderRadius: 1,
                p: 1,
                border: "1px solid #757575",
                background: "#333333",
                "&:hover": {
                  borderColor: "white",
                },
              }}
            >
              <IconButton onClick={handleMinus}>
                <RemoveRoundedIcon />
              </IconButton>
              <Box
                component="input"
                value={betAmount}
                onChange={handleBetAmountChange}
                type="number"
                sx={{
                  background: "none",
                  border: "none",
                  fontSize: "1.5rem",
                  width: "100%",
                }}
              ></Box>
              <IconButton onClick={handlePlus}>
                <AddRoundedIcon />
              </IconButton>
            </Stack>
            <Stack direction="row" spacing={1}>
              <Button
                color="secondary"
                size="small"
                variant="outlined"
                onClick={() => handleMultiply(0.5)}
              >
                <Trans t={t}>/2</Trans>
              </Button>
              <Button
                color="secondary"
                size="small"
                variant="outlined"
                onClick={() => handleMultiply(2)}
              >
                <Trans t={t}>x2</Trans>
              </Button>
            </Stack>
          </Stack>
          {account ? (
            <ButtonBet
              betAmount={betAmount}
              modulo={game.modulo}
              inputs={inputs}
              disabled={isAwaitingWallet || Boolean(gameError)}
              reward={parseFloat(reward)}
            />
          ) : (
            <ButtonConnect />
          )}
        </Stack>
      </Stack>

      <Stack sx={{ px: 4, py: 2, flex: 1, alignItems: "center" }} spacing={4}>
        <Stack
          sx={{ alignItems: "center", width: "100%", flex: 1 }}
          spacing={4}
        >
          <Typography color="secondary">
            {game.modulo === MODULO_COINFLIP && t("Choose coin side to bet on")}
            {game.modulo === MODULO_ROLLADICE &&
              t("Choose the dice number(s) to bet on")}
            {game.modulo === MODULO_TWODICE &&
              t("Choose sum of dices to bet on")}
            {game.modulo === MODULO_ROULETTE && t("Adjust your win chance")}
            {game.modulo === MODULO_ETHEROLL && t("Adjust your win chance")}
          </Typography>
          <GameAnimation game={game} />
          <GameInputs game={game} inputs={inputs} setInputs={setInputs} />
        </Stack>
        <Stack spacing={1.5} sx={{ alignSelf: "flex-start" }}>
          {[
            {
              key: t("Win amount"),
              val: parseAmount(winAmount, selectedNetwork),
              before: (
                <Box
                  component="img"
                  src={selectedNetwork.ui.curIconUrl}
                  height={24}
                  sx={{ mr: 0.5 }}
                />
              ),
              sx: { color: "success.main", fontWeight: "bold" },
            },
            {
              key: t("Win chance"),
              val: winningChance,
              after: "%",
            },
            {
              key: t("Multiplier"),
              val: multiplier,
              before: "x",
            },
          ].map((elem) => (
            <Stack direction={"row"} key={elem.key} spacing={1}>
              <Typography color="secondary">{elem.key}:</Typography>
              <Stack direction="row">
                {elem.before}
                <Box sx={elem.sx}>
                  <AnimatedNumbers animateToNumber={elem.val}></AnimatedNumbers>
                </Box>
                {elem.after}
              </Stack>
            </Stack>
          ))}
        </Stack>
      </Stack>
    </Stack>
  );
}
